/**
 * Interactive map.
 *
 * The SVG is inlined server-side by Twig's `svg()`, so every shape is a real
 * element in the document. A shape's `id` is the slug of its matching entry,
 * which is what ties a panel list item to its shape.
 *
 * Fill colors come from the legend: counties and tribal nations each get their
 * own (Map Legend / Tribal Map Legend), shown only in that mode. Each renders
 * every category exactly once, carrying that category's slug (`legend-1` … `legend-5`).
 * Painting a shape means copying that value onto it as a class; the stylesheet
 * maps each value to a color. Reading the values from the legend rather than
 * repeating them on all 56 county items keeps the legend swatches and the map
 * fills from ever drifting apart.
 *
 * Three modes, driven by the header controls' data-mode:
 *   counties — counties painted by category, tribal nations hidden
 *   tribal — counties stripped back to their muted base, tribal painted
 *   statewide — every county painted with the last legend color, tribal hidden
 *
 * Clicking a panel item or a shape opens a popup anchored to a point inside
 * that shape, with a leader line drawn back to it. Popup content is fetched from
 * Craft's GraphQL API on demand and memoized, so the page ships no hidden
 * per-county markup.
 */

import { computePosition, autoUpdate, offset, flip, shift, size } from '@floating-ui/dom';

interface ShapeLink {
  slug: string;
  label: string;
  category: string;
  item: HTMLElement;
  trigger: HTMLElement;
  shape: SVGGraphicsElement;
  section: string;
  mode: string;
}

interface LegendEntry {
  categorySlug: string;
  legendClass: string;
}

interface Point {
  x: number;
  y: number;
}

interface PopupData {
  title: string | null;
  popupContent: string | null;
}

/**
 *
 * The section has to be part of the query: Flathead exists as both a county and
 * a tribal nation under the same slug, and a slug-only lookup resolves to the
 * tribal entry for both.
 */
const QUERY = [
  'query Popup($slug:[String],$site:[String],$section:[String]){',
  '  entry(slug:$slug,site:$site,section:$section){',
  '    title',
  '    ... on countyMap_default_Entry{ popupContent }',
  '    ... on tribalMap_Entry{ popupContent }',
  '  }',
  '}',
].join('\n');

/**
 * Helper for hiding/showing elements.
 *
 * @param elem Element to hide/show.
 * @param hidden Whether to hide or show the element.
 */
function setHidden(elem: Element | null, hidden: boolean): void {
  if (!elem) {
    return;
  }

  if (hidden) {
    elem.classList.add('hidden');
  } else {
    elem.classList.remove('hidden');
  }
}

/**
 * Read the category-slug -> legend-class lookup out of the legend markup, in the
 * order the legend renders them. The value is a class name the stylesheet resolves
 * to a color, not a color value.
 *
 * Called once per `.map-legend` (one per mode), since each legend section has its
 * own categories and their slugs can collide across sections.
 */
function readLegend(legendElem: ParentNode): LegendEntry[] {
  const entries: LegendEntry[] = [];

  legendElem.querySelectorAll<HTMLElement>('[data-category]').forEach(function (elem) {
    const categorySlug = elem.getAttribute('data-category') || '';
    const legendClass = (elem.getAttribute('data-legend') || '').trim();

    if (categorySlug && legendClass) {
      entries.push({ categorySlug: categorySlug, legendClass: legendClass });
    }
  });

  return entries;
}

/**
 * Find a shape within its group by id.
 *
 * Scoped to the group rather than the document, so the lookup stays correct even
 * with more than one map on the page.
 */
function findShape(group: Element, id: string): SVGGraphicsElement | null {
  return group.querySelector<SVGGraphicsElement>('#' + id);
}

/**
 * Pair a panel's list items with their shapes. Painting happens per mode.
 */
function linkPanel(svg: SVGSVGElement, panel: HTMLElement, section: string): ShapeLink[] {
  const mode = panel.getAttribute('data-panel') || '';
  const groupId = panel.getAttribute('data-map-group') || '';
  const group = groupId ? svg.querySelector('#' + groupId) : null;
  // If the view mode is tribal, the shape ids are prefixed with "tribal-".
  const prefix = mode === 'tribal' ? 'tribal-' : '';

  if (!group) {
    console.warn('[map] SVG has no #' + groupId + ' group');
    return [];
  }

  const links: ShapeLink[] = [];
  const missing: string[] = [];

  panel.querySelectorAll<HTMLElement>('li[data-slug]').forEach(function (item) {
    const slug = item.getAttribute('data-slug') || '';
    const shape = slug ? findShape(group, prefix + slug) : null;

    const label = (item.textContent || '').trim();

    if (!shape) {
      missing.push(slug || label);
      return;
    }

    links.push({
      slug: slug,
      label: label,
      category: item.getAttribute('data-category') || '',
      item: item,
      trigger: item.querySelector<HTMLElement>('.panel-item') || item,
      shape: shape,
      section: section,
      mode: mode,
    });
  });

  if (missing.length) {
    console.warn('[map] ' + groupId + ': no shape for ' + missing.join(', '));
  }

  return links;
}

/**
 * Paint each shape with the color of `category`, or with `override` when one
 * is given (statewide flattens every county to a single color). The
 * data-category attribute is what the stylesheet keys the fill off, so it
 * doubles as the "this shape is painted" flag.
 */
function paint(links: ShapeLink[], legendClasses: Map<string, string>, override?: LegendEntry): void {

  links.forEach(function (link) {
    const category = override ? override.categorySlug : link.category;
    const legendClass = override ? override.legendClass : legendClasses.get(link.category);

    // Always reset first: switching modes can repaint a shape into a different
    // category.
    reset([link]);

    if (legendClass) {
      link.shape.classList.add(legendClass);
      link.shape.setAttribute('data-category', category);
    }
  });
}

/**
 * Drop the legend class, returning shapes to the muted base state in the
 * stylesheet. Every `legend-` class is removed, so
 * a shape can never end up carrying two classes at once.
 */
function reset(links: ShapeLink[]): void {
  links.forEach(function (link) {
    const stale: string[] = [];

    link.shape.classList.forEach(function (name) {
      if (name.indexOf('legend-') === 0) {
        stale.push(name);
      }
    });

    stale.forEach(function (name) {
      link.shape.classList.remove(name);
    });

    link.shape.removeAttribute('data-category');
  });
}

/**
 * How finely a shape's box is sampled when hunting for an interior anchor, and
 * how much room (as a fraction of the shape's smaller side) the box center has
 * to have around it before it is taken at face value. Necessary for oddly shaped
 * counties.
 */
const ANCHOR_SAMPLES = 40;
const ANCHOR_CLEARANCE = 0.08;

/** Anchors, in each shape's own user space — geometry that never changes. */
const anchorCache = new WeakMap<SVGGraphicsElement, Point>();

/**
 * Hit-test a point against a shape's fill. The probe point is passed in and
 * reused: the search below runs thousands of these, and allocating an
 * SVGPoint per test is the expensive part.
 */
function isFilled(shape: SVGGeometryElement, probe: DOMPoint, x: number, y: number): boolean {
  probe.x = x;
  probe.y = y;

  return shape.isPointInFill(probe);
}

/** Whether a point is inside the fill with `radius` to spare in every direction. */
function clears(shape: SVGGeometryElement, probe: DOMPoint, point: Point, radius: number): boolean {
  if (!isFilled(shape, probe, point.x, point.y)) {
    return false;
  }

  for (let i = 0; i < 8; i++) {
    const angle = (Math.PI / 4) * i;

    if (!isFilled(shape, probe, point.x + Math.cos(angle) * radius, point.y + Math.sin(angle) * radius)) {
      return false;
    }
  }

  return true;
}

/**
 * The interior point furthest from any edge — where a cartographer would put
 * the label.
 *
 * Found by sampling a grid across the bounding box and scoring every interior
 * sample by its distance to the nearest exterior one. Brute force, but it runs
 * at most once per shape and only for the shapes the test below rejects.
 * Distances stay squared throughout; only the ordering matters.
 */
function poleOfInaccessibility(shape: SVGGeometryElement, probe: DOMPoint, box: DOMRect): Point | null {
  const stepX = box.width / ANCHOR_SAMPLES;
  const stepY = box.height / ANCHOR_SAMPLES;
  const inside: Point[] = [];
  const outside: Point[] = [];

  for (let i = 0; i <= ANCHOR_SAMPLES; i++) {
    for (let j = 0; j <= ANCHOR_SAMPLES; j++) {
      const point = { x: box.x + i * stepX, y: box.y + j * stepY };

      if (isFilled(shape, probe, point.x, point.y)) {
        inside.push(point);
      } else {
        outside.push(point);
      }
    }
  }

  let best: Point | null = null;
  let bestScore = -1;

  for (let i = 0; i < inside.length; i++) {
    const point = inside[i];

    // The box edge caps the score: a sample can sit far from every exterior
    // *sample* and still be hard up against the shape's own boundary, which is
    // where the box clips it.
    const edge = Math.min(
      point.x - box.x,
      box.x + box.width - point.x,
      point.y - box.y,
      box.y + box.height - point.y
    );

    let score = edge * edge;

    for (let j = 0; j < outside.length; j++) {
      const dx = point.x - outside[j].x;
      const dy = point.y - outside[j].y;
      const distance = dx * dx + dy * dy;

      if (distance < score) {
        score = distance;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      best = point;
    }
  }

  return best;
}

/**
 * Where a shape's popup points, in the shape's own user space.
 *
 * The center of the bounding box is the obvious answer and is wrong for the
 * awkward counties: Rosebud wraps around Treasure, so its box center falls in a
 * neighboring county and drags the dot and the leader line along with it. The
 * center is therefore only kept when it, and a small ring around it, is really
 * inside the fill — which is the common case and costs nine hit tests.
 */
function localAnchor(shape: SVGGraphicsElement): Point {
  const cached = anchorCache.get(shape);

  if (cached) {
    return cached;
  }

  const box = shape.getBBox();
  const centre = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
  const geometry = shape as SVGGeometryElement;
  const svg = shape.ownerSVGElement;

  // A hidden shape measures as empty; don't cache that as its anchor.
  if (!svg || !box.width || !box.height || typeof geometry.isPointInFill !== 'function') {
    return centre;
  }

  const probe = svg.createSVGPoint();
  const clearance = Math.min(box.width, box.height) * ANCHOR_CLEARANCE;
  const anchor = clears(geometry, probe, centre, clearance)
    ? centre
    : poleOfInaccessibility(geometry, probe, box) || centre;

  anchorCache.set(shape, anchor);

  return anchor;
}

/**
 * The shape's anchor in viewport coordinates, so it can be mixed with the
 * `getBoundingClientRect()` values the popup and the host are measured in.
 */
function anchorPoint(shape: SVGGraphicsElement): Point {
  const svg = shape.ownerSVGElement;
  const ctm = shape.getScreenCTM();

  // Runs on every autoUpdate frame, so the box is only measured when the
  // transform is unavailable and there is nothing else to fall back on.
  if (!svg || !ctm) {
    const box = shape.getBoundingClientRect();

    return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
  }

  const local = localAnchor(shape);
  const point = svg.createSVGPoint();
  point.x = local.x;
  point.y = local.y;

  const screen = point.matrixTransform(ctm);

  return { x: screen.x, y: screen.y };
}

/**
 * Where the popup points. Collapse the shape to its anchor point for Floating
 * UI's `offset()`, so the configured gap is the literal distance from the point
 * the leader line starts at.
 *
 * `contextElement` is what lets `autoUpdate` observe the real shape for
 * resizes and ancestor scrolling.
 */
function anchorFor(shape: SVGGraphicsElement) {
  return {
    contextElement: shape,
    getBoundingClientRect() {
      const point = anchorPoint(shape);

      return {
        x: point.x,
        y: point.y,
        top: point.y,
        bottom: point.y,
        left: point.x,
        right: point.x,
        width: 0,
        height: 0,
      };
    },
  };
}

/**
 * The point on `rect`'s border where a line coming from `from` meets it.
 *
 * Solving this geometrically rather than switching on Floating UI's resolved
 * placement means `flip()` and `shift()` are handled for free — the line always
 * lands on whichever edge actually faces the shape.
 */
function edgePoint(from: Point, rect: DOMRect): Point {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const dx = cx - from.x;
  const dy = cy - from.y;

  if (dx === 0 && dy === 0) {
    return { x: cx, y: cy };
  }

  const scaleX = dx !== 0 ? rect.width / 2 / Math.abs(dx) : Infinity;
  const scaleY = dy !== 0 ? rect.height / 2 / Math.abs(dy) : Infinity;
  const scale = Math.min(scaleX, scaleY);

  return { x: cx - dx * scale, y: cy - dy * scale };
}

/**
 * Build the popup markup from the GraphQL payload.
 *
 */
function renderPopup(data: PopupData): string {

  let popupContent = '<h2 id="map-popup-title">' + data.title + '</h2>'

  if (data.popupContent) {
    popupContent += '<div class="map-popup-description">' + data.popupContent + '</div>'
  }

  return popupContent;
}

/**
 * Wire up one `.interactive-map` section.
 *
 * Everything is scoped to `root`, so several maps can share a page. The work
 * happens in three passes:
 *   1. Setup — read the legends, pair panel items with their shapes, and look
 *      up every element the rest of the function touches.
 *   2. Popup — open/close/position the popup and its leader line, and bind
 *      the shapes, panel items, and selects that open it.
 *   3. Modes — applyMode() switches between counties, tribal, and statewide by
 *      repainting shapes and showing that mode's panel, select, and legend.
 */
function initMap(root: HTMLElement): void {
  // --- Setup ---------------------------------------------------------------

  // Every shape lookup goes through the inlined SVG, so without it there is
  // nothing to paint or click.
  const svg = root.querySelector<SVGSVGElement>('.map-container svg');

  if (!svg) {
    console.warn('[map] no inlined SVG found');
    return;
  }

  // Each mode's legend, keyed by its data-legend-mode, along with the
  // category-slug -> legend-class lookup built from it. The legends are kept
  // apart because Map Legend and Tribal Map Legend are separate sections, and
  // Craft only keeps slugs unique within a section; one shared lookup would
  // let a tribal category with the same slug overwrite a county one.
  //
  // `legends` keeps the entries in render order (statewide needs the last
  // one); `legendClasses` is what paint() consults per shape.
  const legends: { [mode: string]: LegendEntry[] } = {};
  const legendClasses: { [mode: string]: Map<string, string> } = {};
  const legendViews = root.querySelectorAll<HTMLElement>('.map-legend[data-legend-mode]');

  legendViews.forEach(function (view) {
    const legendMode = view.getAttribute('data-legend-mode') || '';
    const entries = readLegend(view);
    const classes = new Map<string, string>();

    entries.forEach(function (entry) {
      classes.set(entry.categorySlug, entry.legendClass);
    });

    if (!entries.length) {
      console.warn('[map] ' + legendMode + ' legend supplied no classes — shapes stay unpainted');
    }

    legends[legendMode] = entries;
    legendClasses[legendMode] = classes;
  });

  // Pulled out by name for applyMode(). The empty fallbacks cover a site with
  // no tribal nations, where the template leaves the tribal legend out, so
  // painting just finds no classes rather than throwing.
  const countyLegend = legends['counties'] || [];
  const countyClasses = legendClasses['counties'] || new Map<string, string>();
  const tribalClasses = legendClasses['tribal'] || new Map<string, string>();

  // The two top-level SVG groups. Counties always show (they are the base
  // layer); the tribal group overlays them and only shows in tribal mode.
  const countyGroup = svg.querySelector('#county-map');
  const tribalGroup = svg.querySelector('#tribal-nations-map');

  // Link every panel once up front, so switching modes only repaints.
  const links: { [mode: string]: ShapeLink[] } = {};

  // Which SVG group each mode paints, read off the panel that lists it. The
  // selects are labelled with the same group, which is how the right one is
  // shown — and why statewide, whose panel names no group, resolves to nothing.
  const groups: { [mode: string]: string } = {};

  root.querySelectorAll<HTMLElement>('.panel-view[data-map-group]').forEach(function (panel) {
    const mode = panel.getAttribute('data-panel') || '';
    groups[mode] = panel.getAttribute('data-map-group') || '';
    links[mode] = linkPanel(svg, panel, mode === 'tribal' ? 'tribalMap' : 'countyMap');
  });

  // Panel <-> shape pairs per mode. Either can be empty when the template
  // skipped that panel (no entries in the section).
  const counties = links['counties'] || [];
  const tribal = links['tribal'] || [];

  // Everything applyMode() shows, hides, or flags as each mode switches.
  const controls = root.querySelectorAll('.map-controls [data-mode]');
  const panels = root.querySelectorAll('.panel-view');
  const legendElem = root.querySelector('.interactive-map-legend');
  const selectWrap = root.querySelector<HTMLElement>('.map-select');
  const selectViews = root.querySelectorAll<HTMLElement>('.map-select [data-map-group]');
  const selects = root.querySelectorAll<HTMLSelectElement>('.map-select select');

  // --- Popup ---------------------------------------------------------------

  const host = root.querySelector<HTMLElement>('.interactive-map-content');
  const popup = root.querySelector<HTMLElement>('.map-popup');
  const popupBody = root.querySelector<HTMLElement>('.map-popup-body');
  const closeBtn = root.querySelector<HTMLElement>('.map-popup-close');
  const leader = root.querySelector<SVGSVGElement>('.map-leader');
  const leaderLine = leader ? leader.querySelector<SVGLineElement>('line') : null;
  const leaderDot = leader ? leader.querySelector<SVGCircleElement>('circle') : null;

  // Distance in px between the shape and the popup on desktop.
  const gap = Number(root.getAttribute('data-popup-offset') || 24);
  // The Craft site handle, so the GraphQL query returns this site's popup copy.
  const site = root.getAttribute('data-map-site') || 'default';
  // Above this the popup is anchored beside its shape; below it, centered.
  const desktop = window.matchMedia('(min-width: 950px)');

  // The link whose popup is open, or null when it is closed.
  let current: ShapeLink | null = null;
  // Cleanup for the Floating UI autoUpdate() watching the open popup.
  let stopAutoUpdate: (() => void) | null = null;
  // The element that opened the popup, so focus can go back to it on close.
  let opener: HTMLElement | SVGElement | null = null;
  // The active mode. open() checks it so only that mode's shapes respond.
  let mode = '';

  /**
   * No memo here on purpose: Craft caches GraphQL results server-side (keyed by
   * schema, query and variables, and invalidated whenever an element is saved),
   * so a repeat request is already cheap and can never serve stale content.
   */
  function fetchPopup(link: ShapeLink): Promise<string> {
    return fetch('/actions/graphql/api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: { slug: [link.slug], site: [site], section: [link.section] },
      }),
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .then(function (payload) {
        if (payload.errors && payload.errors.length) {
          throw new Error(payload.errors[0].message);
        }

        if (!payload.data || !payload.data.entry) {
          throw new Error('no entry for ' + link.slug);
        }

        return renderPopup(payload.data.entry as PopupData);
      });
  }

  /**
   * Put the middle of the map at the middle of the screen.
   *
   * Animated rather than jumped, so the reader can see where they have been
   * taken. The popup rides along for free: it is centred on the map by the
   * stylesheet, so it stays glued to it for the whole of the scroll.
   */
  function centerMap(): void {
    const container = root.querySelector('.map-container');

    if (!container) {
      return;
    }

    const box = container.getBoundingClientRect();
    const target = window.scrollY + box.top + box.height / 2 - window.innerHeight / 2;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: Math.max(0, target), behavior: reduced ? 'instant' : 'smooth' });
  }

  /**
   * Draw the connector from the shape's anchor point to the popup's near edge, in
   * coordinates local to `.interactive-map-content`.
   */
  function drawLeader(shape: SVGGraphicsElement): void {
    if (!leader || !leaderLine || !leaderDot || !popup || !host) {
      return;
    }

    const hostRect = host.getBoundingClientRect();
    const from = anchorPoint(shape);
    const to = edgePoint(from, popup.getBoundingClientRect());

    leaderLine.setAttribute('x1', String(from.x - hostRect.left));
    leaderLine.setAttribute('y1', String(from.y - hostRect.top));
    leaderLine.setAttribute('x2', String(to.x - hostRect.left));
    leaderLine.setAttribute('y2', String(to.y - hostRect.top));

    // The dot marks the map end of the line, sitting on the shape's anchor.
    leaderDot.setAttribute('cx', String(from.x - hostRect.left));
    leaderDot.setAttribute('cy', String(from.y - hostRect.top));

    leader.removeAttribute('hidden');
  }

  /**
   * Place the popup for `link`: beside its shape on desktop, centered on the map
   * below the breakpoint. Called on open and again whenever the breakpoint is
   * crossed, so it always tears down the previous autoUpdate() first.
   */
  function position(link: ShapeLink): void {
    if (!popup) {
      return;
    }

    if (stopAutoUpdate) {
      stopAutoUpdate();
      stopAutoUpdate = null;
    }

    // Below the breakpoint there is nothing to anchor to and no room for a
    // connector, so the popup is centered on the map, and the stylesheet owns
    // placement entirely. Both inline properties have to go: they are written by
    // the desktop path, and inline beats the stylesheet — a popup open while the
    // breakpoint is crossed would otherwise keep its anchored offset and the
    // height cap size() measured against the desktop band.
    if (!desktop.matches) {
      popup.classList.add('is-centered');
      popup.style.removeProperty('transform');
      popup.style.removeProperty('max-height');
      if (leader) {
        leader.setAttribute('hidden', '');
      }
      centerMap();
      return;
    }

    popup.classList.remove('is-centered');

    const anchor = anchorFor(link.shape);

    // Everything is bounded by the map band rather than the viewport. Without
    // this a tall county card (Lincoln's runs ~520px against a ~480px band)
    // floats above the map, stretching the leader line and reading as detached.
    // size() then caps it to whatever height is actually available, so the
    // overflow becomes an internal scroll instead.
    const boundary = host || undefined;

    // `rootBoundary: 'document'` drops the viewport from the overflow test, so
    // flipping, shifting and sizing answer to the map band alone. That makes
    // the placement independent of where the page happens to be scrolled to:
    // the popup is anchored to a county, and scrolling past the map must not
    // re-place it or squeeze its height on the way through.
    const root = 'document' as const;

    stopAutoUpdate = autoUpdate(
      anchor,
      popup,
      function () {
        computePosition(anchor, popup, {
          placement: 'right',
          middleware: [
            offset(gap),
            flip({ boundary: boundary, rootBoundary: root }),
            shift({ padding: 12, boundary: boundary, rootBoundary: root }),
            size({
              boundary: boundary,
              rootBoundary: root,
              padding: 12,
              apply({ availableHeight, elements }) {
                elements.floating.style.maxHeight = Math.max(240, availableHeight) + 'px';
              },
            }),
          ],
        }).then(function (result) {
          popup.style.transform = 'translate(' + Math.round(result.x) + 'px,' + Math.round(result.y) + 'px)';
          drawLeader(link.shape);
        });
      },
      // Nothing above depends on the scroll offset, so recomputing on scroll
      // could only introduce rounding drift. Resizes still update, which is
      // what the fetched content landing in the popup needs.
      { ancestorScroll: false, layoutShift: false }
    );
  }

  /**
   * Close the open popup, if there is one, and undo everything open() set: the
   * selected shape, aria-expanded, the leader, the selects, and focus.
   */
  function close(): void {
    if (!current || !popup) {
      return;
    }

    if (stopAutoUpdate) {
      stopAutoUpdate();
      stopAutoUpdate = null;
    }

    current.shape.removeAttribute('data-selected');
    current.trigger.setAttribute('aria-expanded', 'false');

    popup.setAttribute('hidden', '');
    popup.classList.remove('is-centered');
    if (leader) {
      leader.setAttribute('hidden', '');
    }

    // Back to the neutral option, so the same county can be picked again.
    syncSelects('');

    // Focus goes back to whatever was used to open the popup, not always to the
    // panel item: below the breakpoint the panel is display:none, so returning
    // focus there would drop it.
    const returnTo = opener || current.trigger;
    current = null;
    opener = null;
    returnTo.focus();
  }

  /**
   * Open the popup for `link`, or close it if it is already open. `from` is the
   * element the reader used (shape, panel item, or select); focus goes back to it
   * when the popup closes.
   */
  function open(link: ShapeLink, from?: HTMLElement | SVGElement): void {
    if (!popup || !popupBody) {
      return;
    }

    // Only the active mode's shapes can open a popup. The click listeners stay
    // bound for the life of the page, so without this a county is still clickable
    // underneath the tribal overlay — and statewide, which owns no links at all,
    // opens nothing.
    if (link.mode !== mode) {
      return;
    }

    // Toggle off when the same shape is clicked again.
    if (current === link) {
      close();
      return;
    }

    // Switching straight from one shape to another: clear the old selection
    // by hand rather than through close(), which would also hide the popup and
    // move focus only for this function to undo both.
    if (current) {
      current.shape.removeAttribute('data-selected');
      current.trigger.setAttribute('aria-expanded', 'false');
      if (stopAutoUpdate) {
        stopAutoUpdate();
        stopAutoUpdate = null;
      }
    }

    current = link;
    opener = from || link.trigger;
    link.shape.setAttribute('data-selected', '');
    link.trigger.setAttribute('aria-expanded', 'true');

    // Mirror the choice in the select, so the one on screen reads as the
    // current selection however the popup was opened. Assigning a slug a
    // select does not list — a county to the tribal one — leaves that select
    // on its neutral option.
    syncSelects(link.slug);

    // Show a placeholder first so the popup has a box to position, then let
    // autoUpdate re-run when the fetched content changes its size.
    popupBody.innerHTML = '<p class="map-popup-loading">Loading ' +link.label + '…</p>';
    popup.removeAttribute('hidden');
    position(link);

    // `preventScroll` because focusing something inside the popup would scroll it
    // into view, which on mobile lands on top of the scroll position() just
    // chose, and on desktop drags the page towards a popup that is already
    // beside the county the reader clicked.
    if (closeBtn) {
      closeBtn.focus({ preventScroll: true });
    }

    // A response can land after the reader has moved on to another shape or
    // closed the popup, so each handler first checks this link is still the
    // open one before writing into the shared popup body.
    fetchPopup(link)
      .then(function (html) {
        if (current !== link) {
          return;
        }
        popupBody.innerHTML = html;
      })
      .catch(function (error) {
        if (current !== link) {
          return;
        }
        console.warn('[map] popup fetch failed for ' + link.slug, error);
        popupBody.innerHTML =
          '<h2 id="map-popup-title">' + link.label + '</h2>' +
          '<p class="map-popup-error">Sorry — this information could not be loaded.</p>';
      });
  }

  /**
   * Turn shapes into controls, or back into plain artwork. The stylesheet keys
   * cursor, hover, and focus styling off `path[role="button"]`, so dropping the
   * attribute removes the behavior.
   */
  function setInteractive(links: ShapeLink[], on: boolean): void {
    links.forEach(function (link) {
      if (on) {
        link.shape.setAttribute('role', 'button');
        link.shape.setAttribute('tabindex', '0');
        link.shape.setAttribute('aria-label', link.label);
      } else {
        link.shape.removeAttribute('role');
        link.shape.removeAttribute('tabindex');
        link.shape.removeAttribute('aria-label');
      }
    });
  }

  /**
   * Attach the listeners that open `link`'s popup: click and Enter/Space on the
   * shape, and click on its panel item.
   */
  function bind(link: ShapeLink): void {
    // The control attributes are applied per mode by setInteractive(), not here:
    // statewide has nothing county-specific to show, so its shapes are inert.
    // Listeners are attached once and gated by `mode`.
    link.shape.addEventListener('click', function () {
      open(link, link.shape);
    });

    link.shape.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        open(link, link.shape);
      }
    });

    link.trigger.addEventListener('click', function () {
      open(link, link.trigger);
    });
  }

  // Bind both modes' links up front; open() ignores whichever mode is inactive.
  counties.concat(tribal).forEach(bind);

  /**
   * The links the given mode owns. Statewide owns none — it paints every county
   * one color and has nothing county-specific to open.
   */
  function linksFor(forMode: string): ShapeLink[] {
    if (forMode === 'tribal') {
      return tribal;
    }

    return forMode === 'counties' ? counties : [];
  }

  /** Show the same choice in every select, whichever one is on screen. */
  function syncSelects(slug: string): void {
    selects.forEach(function (select) {
      select.value = slug;
    });
  }

  // The selects stand in for the panel lists below the breakpoint. Picking a
  // slug opens that popup; picking the neutral option closes it.
  selects.forEach(function (select) {
    select.addEventListener('change', function () {
      const slug = select.value;

      if (!slug) {
        close();
        return;
      }

      // Only the active mode's select is on screen, so its options and
      // linksFor(mode) are always describing the same list.
      linksFor(mode).forEach(function (link) {
        if (link.slug === slug) {
          open(link, select);
        }
      });
    });
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', close);
  }

  // Escape closes the popup from anywhere on the page, as a dialog should.
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && current) {
      close();
    }
  });

  // Clicking away closes, but only outside both the popup and the map itself —
  // clicks on another shape are handled by that shape's own listener.
  document.addEventListener('click', function (event) {
    if (!current || !popup) {
      return;
    }

    const target = event.target as Node;

    if (popup.contains(target) || svg.contains(target)) {
      return;
    }

    // The controls that open popups are outside the popup and the map, so a
    // click on one must not be read as a click away from what it just opened —
    // browsers dispatch a click on a <select> along with its change event.
    if (target instanceof Element && target.closest('.panel-item, .map-select')) {
      return;
    }

    close();
  });

  // Crossing the breakpoint changes the whole placement strategy.
  const onBreakpoint = function () {
    if (current) {
      position(current);
    }
  };

  desktop.addEventListener('change', onBreakpoint);
  
  // --- Modes ---------------------------------------------------------------

  /**
   * Switch the map to `next` ('counties', 'tribal', or 'statewide'). Each mode
   * owns a panel, a select, a legend, and a set of painted, clickable shapes;
   * this shows the active mode's set and hides or resets the others.
   */
  function applyMode(next: string): void {
    // Any open popup belongs to the outgoing mode's shapes.
    close();
    mode = next;

    // Panels and controls.
    panels.forEach(function (panel) {
      setHidden(panel, panel.getAttribute('data-panel') !== mode);
    });
    controls.forEach(function (control) {
      control.setAttribute('aria-pressed', String(control.getAttribute('data-mode') === mode));
    });

    // The map itself. Each mode paints from its own legend's lookup:
    //   tribal    — counties fall back to the muted base under the overlay, and
    //               tribal nations are colored from the Tribal Map Legend.
    //   statewide — every county gets the county legend's last (strongest)
    //               color, since statewide is one figure, not a per-county scale.
    //   counties  — counties are colored by category from the Map Legend.
    if (mode === 'tribal') {
      reset(counties);
      paint(tribal, tribalClasses);
    } else if (mode === 'statewide') {
      reset(tribal);
      paint(counties, countyClasses, countyLegend[countyLegend.length - 1]);
    } else {
      reset(tribal);
      paint(counties, countyClasses);
    }

    // Counties stay visible in every mode as the base layer; only the tribal
    // overlay toggles.
    setHidden(countyGroup, false);
    setHidden(tribalGroup, mode !== 'tribal');

    // Only the shapes the current mode can actually open a popup for are
    // controls; statewide has none.
    setInteractive(counties, mode === 'counties');
    setInteractive(tribal, mode === 'tribal');

    // Each mode shows its own legend, matched on data-legend-mode. Statewide
    // has none (it doesn't use a per-category scale), so `legends[mode]` is
    // undefined and the whole legend block, header included, is hidden.
    legendViews.forEach(function (view) {
      setHidden(view, view.getAttribute('data-legend-mode') !== mode);
    });
    setHidden(legendElem, !legends[mode]);

    // Same for the selects: each stands in for a panel list below the
    // breakpoint, so the one carrying this mode's group is shown and the rest
    // are hidden. Statewide owns no group, so nothing matches and the whole
    // control goes with it.
    selectViews.forEach(function (view) {
      setHidden(view, view.getAttribute('data-map-group') !== groups[mode]);
    });
    setHidden(selectWrap, !groups[mode]);
  }

  // Each header button switches to the mode named in its data-mode.
  controls.forEach(function (control) {
    control.addEventListener('click', function () {
      applyMode(control.getAttribute('data-mode') || '');
    });
  });

  // Start in whichever mode the first button names. The template only renders
  // the Counties/Tribal buttons when those sections have entries, so this is
  // the first mode that has something to show.
  const initial = controls.length ? controls[0].getAttribute('data-mode') || 'counties' : 'counties';
  applyMode(initial);
}

// Initialize maps with support for multiple maps on the same page
function init(): void {
  document.querySelectorAll<HTMLElement>('.interactive-map').forEach((elem) => {
    initMap(elem);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
